import { Box, ClickAwayListener, Grid, TextField } from '@mui/material'
import { ChangeEvent, ReactNode, useState } from 'react'
import * as Styled from '../styled'
import { PopupInstance } from 'components/popup/Popup'
import { EventReminder } from 'types/api/changes'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import Button from 'components/buttons/Button'
import { TickIcon } from 'assets/icons'
import { t, use } from 'i18next'
import Logger from 'services/Logger'

interface ContentProps {
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  instance?: PopupInstance | null | undefined
  editingReminder?: EventReminder | undefined
  setOpen?: (b: boolean) => void
}

export enum ReminderType {
  MIN,
  HOUR,
  DAY,
  WEEK
}

interface CustomReminder {
  type: ReminderType
  title: string
}

export function CustomReminderContent(props: ContentProps) {
  const [searchText, setSearchText] = useState<string>(
    props.editingReminder?.offset !== undefined
      ? offsetToText(props.editingReminder)
      : '15'
  )

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const count = Number(searchText)

  const reminderTypes: CustomReminder[] = [
    {
      type: ReminderType.MIN,
      title: t('timeBefore', { time: t('minute', { count }) })
    },
    {
      type: ReminderType.HOUR,
      title: t('timeBefore', { time: t('hour', { count }) })
    },
    {
      type: ReminderType.DAY,
      title: t('timeBefore', { time: t('day', { count }) })
    },
    {
      type: ReminderType.WEEK,
      title: t('timeBefore', { time: t('week', { count }) })
    }
  ]
  const [currentType, setCurrentType] = useState<CustomReminder>(
    props.editingReminder !== undefined
      ? (reminderTypes.find((type) => {
          return (
            type.type ===
            convertSecondsToClosestType(
              props.editingReminder?.offset ?? 60 * 15
            )
          )
        }) ?? reminderTypes[0])
      : reminderTypes[0]
  )

  const showError =
    convertToMinutes(searchText, currentType.type) >
    convertToMinutes('4', ReminderType.WEEK)

  function addReminderToEvent(type: ReminderType) {
    if (showError) return

    Logger.pink('Reminder:', searchText, type)

    let minutes = convertToMinutes(searchText, type)

    Logger.pink('Reminder:', minutes)

    const reminder: EventReminder = {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: minutes * 60 // store offset in seconds
    }

    props.setReminders([
      ...(props.reminders.filter((reminder) => {
        return reminder.offset !== props.editingReminder?.offset
      }) ?? []),
      reminder
    ])
    Logger.pink('Reminder:', props.reminders)
    props.instance?.hide()
    if (props.setOpen) props.setOpen(false)
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        console.log('hide')
        props.instance?.hide()
        props.setOpen?.(false)
      }}
    >
      <Grid container alignItems={'start'} direction={'column'}>
        <PopupSection title={`${t('customReminder')}`}>
          <Box height={8}></Box>
          <TextField
            autoFocus
            helperText={showError ? `${t('reminderNotValid')}` : ''}
            placeholder="0"
            error={showError}
            type={'number'}
            value={searchText}
            onChange={onSearchChange}
            variant="standard"
          />
          {reminderTypes.map((customRem) => {
            return (
              <PopupSectionRow
                action={() => {
                  setCurrentType(customRem)
                }}
                value={
                  customRem.type === currentType.type ? <TickIcon /> : <></>
                }
                key={customRem.type}
                title={customRem.title}
              />
            )
          })}
          <Box height={8}></Box>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <Button
              size="xs"
              onClick={addReminderToEvent.bind(null, currentType.type)}
            >
              {t('done')}
            </Button>
          </div>
        </PopupSection>
      </Grid>
    </ClickAwayListener>
  )
}

interface CustomEventReminderPopupProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void

  edititngReminder?: EventReminder | undefined
}

export default function CustomEventReminderPopup(
  props: CustomEventReminderPopupProps
) {
  return (
    <ClickAwayListener
      onClickAway={() => {
        props.setOpen(false)
      }}
    >
      <Styled.DatePickPopup
        open={props.open}
        content={
          <CustomReminderContent
            {...props}
            editingReminder={props.edititngReminder}
            setOpen={props.setOpen}
          />
        }
        placement={'bottom'}
      >
        {props.children}
      </Styled.DatePickPopup>
    </ClickAwayListener>
  )
}

function convertToMinutes(text: string, type: ReminderType) {
  let minutes = 0
  let typed = Number(text)

  switch (type) {
    case ReminderType.MIN:
      minutes = typed * 1
      break
    case ReminderType.HOUR:
      minutes = typed * 60
      break
    case ReminderType.DAY:
      minutes = typed * 60 * 24
      break
    case ReminderType.WEEK:
      minutes = typed * 60 * 24 * 7
      break
  }

  return minutes
}

function convertSecondsToClosestType(seconds: number): ReminderType {
  const SECONDS_IN_HOUR = 3600
  const SECONDS_IN_DAY = 86400
  const SECONDS_IN_WEEK = 604800

  if (seconds % (60 * 60 * 24 * 7) == 0) {
    return ReminderType.WEEK
  } else if (seconds % (60 * 60 * 24) == 0) {
    return ReminderType.DAY
  } else if (seconds % (60 * 60) == 0) {
    return ReminderType.HOUR
  } else if (seconds % 60 == 0) {
    return ReminderType.MIN
  } else {
    if (seconds < SECONDS_IN_HOUR) {
      return ReminderType.MIN
    } else if (seconds <= SECONDS_IN_DAY) {
      return ReminderType.HOUR
    } else if (seconds <= SECONDS_IN_WEEK) {
      return ReminderType.DAY
    } else {
      return ReminderType.WEEK
    }
  }
}
function offsetToText(editingReminder: EventReminder): string {
  const type = convertSecondsToClosestType(editingReminder.offset)

  let minutes = editingReminder.offset / 60
  let hours = minutes / 60
  let days = hours / 24
  let weeks = days / 7

  switch (type) {
    case ReminderType.MIN:
      return minutes.toString()
    case ReminderType.HOUR:
      return hours.toString()
    case ReminderType.DAY:
      return days.toString()
    case ReminderType.WEEK:
      return weeks.toString()
  }
}
